import { Fragment } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { IAssignmentItem, ISectionAssignments } from '../../types';

interface IVariableAssignmentsTableProps {
  productKey: string;
  sections: ISectionAssignments;
}

/**
 * Renders a table displaying assignment items organized by sections
 * @description Shared table component used by ConflictDialog, DefaultAssignmentsDialog, and AutoSelectedDialog
 * @param {IVariableAssignmentsTableProps} props - Component props
 * @returns {JSX.Element} Assignment table component
 */
export const VariableAssignmentsTable: React.FC<IVariableAssignmentsTableProps> = ({
  productKey,
  sections,
}) => {
  const { t } = useTranslation();

  return (
    <div className="productSection" key={productKey}>
      {/* Product Key Header (e.g., "722223#Azurion 7 M12") */}
      <div className="productHeader">{productKey}</div>

      {/* Single Table for entire product with column headers at top */}
      <TableContainer className="productTableContainer">
        <Table aria-label={`assignments table for ${productKey}`}>
          <TableHead>
            <TableRow>
              <TableCell
                className="tabCell tabHead feature-column feature-header"
                variant="head"
                align="left"
              >
                {t('labels.feature')}
              </TableCell>
              <TableCell
                className="tabCell tabHead option-column feature-header"
                variant="head"
                align="left"
              >
                {t('labels.option')}
              </TableCell>
              <TableCell
                className="tabCell tabHead description-column description-header"
                variant="head"
                align="left"
              >
                {t('labels.description')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Iterate through sections within the product */}
            {Object.entries(sections).map(([sectionName, items]: [string, IAssignmentItem[]]) => (
              <Fragment key={sectionName}>
                {/* Section Name Subheader Row */}
                <TableRow className="sectionSubheader">
                  <TableCell
                    colSpan={3}
                    className="tabCell"
                    style={{ fontWeight: 'bold', fontSize: '0.95em' }}
                  >
                    {sectionName}
                  </TableCell>
                </TableRow>
                {/* Items for this section */}
                {items.map((item: IAssignmentItem, index: number) => (
                  <TableRow
                    key={`${productKey}_${sectionName}_${index}`}
                    className="tableRow"
                    hover
                  >
                    <TableCell className="tabCell feature-column" align="left">
                      {item.featureId}
                    </TableCell>
                    <TableCell className="tabCell option-column" align="left">
                      {item.optionId}
                    </TableCell>
                    <TableCell className="tabCell description-column" align="left">
                      {item.name}
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
